import { useMemo } from 'react';
import { isBlockVisible, liveOnly, type LiveTimeBlock } from '@wiztime/core';
import { queries } from '@wiztime/storage';
import { useLive } from './useLive';
import { useNow } from './useNow';
import { useStore } from './useServices';
import { useSettings } from './useSettings';

export interface VisibleBlocks {
  /** Live blocks of the date, expired drafts removed, ordered by start. */
  readonly blocks: readonly LiveTimeBlock[];
  readonly loading: boolean;
  readonly error: unknown;
  readonly retry: () => void;
}

const NO_BLOCKS: readonly LiveTimeBlock[] = [];

/** The blocks and drafts a personal date shows (draft expiry is computed from the clock). */
export function useVisibleBlocks(date: string): VisibleBlocks {
  const store = useStore();
  const settings = useSettings();
  const now = useNow(60_000);
  const { data, loading, error, retry } = useLive(
    () => queries.blocksForDate(store.repo, date),
    [store, date],
  );
  return useMemo(() => {
    const blocks =
      data === undefined ? NO_BLOCKS : liveOnly(data.filter((block) => isBlockVisible(block, settings, now)));
    return { blocks, loading, error, retry };
  }, [data, settings, now, loading, error, retry]);
}
